import { normalizeWidgetParams } from "@shared/utils/urls/widget-params";
import { EventEmitter } from "events";
import { queryParamSerializer } from "@shared/utils/url";
import {
  isIframeMessage as isIframeUtilMessage,
  setupIframeHeightListener,
} from "@shared/utils/iframe-utils/iframe-resize";
import {
  CUSTOMER_ATTRIBUTION_DISCARDED_MESSAGE,
  CUSTOMER_ATTRIBUTION_ESTABLISHED_MESSAGE,
  CUSTOMER_ATTRIBUTION_INITIALIZE_MESSAGE,
  CUSTOMER_ATTRIBUTION_PARENT_ORIGIN_QUERY_PARAM,
  CUSTOMER_ATTRIBUTION_QUERY_PARAM,
  CUSTOMER_ATTRIBUTION_READY_MESSAGE,
  CUSTOMER_ATTRIBUTION_REFRESH_REQUEST_MESSAGE,
  CUSTOMER_ATTRIBUTION_REFRESH_RESPONSE_MESSAGE,
  CUSTOMER_ATTRIBUTION_REFRESH_UNAVAILABLE_MESSAGE,
  CUSTOMER_ATTRIBUTION_TRANSPORT_ID_QUERY_PARAM,
  type CustomerAttributionMessage,
  isCustomerAttributionMessage,
} from "@shared/modules/basket/session/customer-attribution-messaging";
import {
  type CustomerAttributionTokenRefresh,
  WidgetDatalessEvents,
  WidgetQueryParams,
} from "./Widget-types";

const bodyClass = "showpass-widget-body";
const widgetID = "showpass-widget";
const iFrameClass = "showpass-widget-iframe";
const animationDuration = 300;

function createCustomerAttributionTransportId(): string | undefined {
  if (typeof window.crypto?.getRandomValues !== "function") return undefined;

  const randomValues = new Uint32Array(4);
  window.crypto.getRandomValues(randomValues);
  return Array.from(randomValues, (value) =>
    value.toString(16).padStart(8, "0")
  ).join("");
}

function getCustomerAttributionParentOrigin(): string | undefined {
  const { origin } = window.location;
  return origin && origin !== "null" ? origin : undefined;
}

interface CloseOptions {
  removeBodyClass?: boolean;
  remount?: boolean;
}

interface BackToCalendarEvent {
  venueId: string;
  params: WidgetQueryParams;
}

interface CalendarEventPurchaseEvent {
  slug: string;
  params: WidgetQueryParams;
  containerId: string;
}

type CustomerAttributionRefreshRequest = Extract<
  CustomerAttributionMessage,
  { type: typeof CUSTOMER_ATTRIBUTION_REFRESH_REQUEST_MESSAGE }
>;

interface BackToEventEvent {
  slug: string;
  params: WidgetQueryParams;
}

type CallbackWithOptionalArgs<T> = ((args: T) => void) | (() => void);

export interface Widget extends EventEmitter {
  on(event: WidgetDatalessEvents, callback: () => void): this;
  on(
    event: "showpass.event-purchase.back-to-calendar",
    callback: CallbackWithOptionalArgs<BackToCalendarEvent>
  ): this;
  on(
    event: "showpass.calendar.event-purchase",
    callback: CallbackWithOptionalArgs<CalendarEventPurchaseEvent>
  ): this;
  on(
    event: "showpass.checkout.back-to-event",
    callback: CallbackWithOptionalArgs<BackToEventEvent>
  ): this;
}

export interface WidgetOptions {
  src: string;
  containerId?: string;
  queryParams?: Record<string, unknown>;
  enableIframeResize?: boolean;
  onCustomerAttributionReleased?: () => void;
  onCustomerAttributionTokenRefresh?: CustomerAttributionTokenRefresh;
  /** Internal hook used to synchronize trusted parameters after acceptance. */
  onCustomerAttributionTokenRefreshAccepted?: (token: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export class Widget extends EventEmitter {
  isModalOpen = false;
  isEmbedded = false;

  iframeContainer: HTMLDivElement;

  iFrame: HTMLIFrameElement;

  spinner: HTMLDivElement;

  scrollTop: number;

  cleanupHeightListener: () => void = noop;

  enableIframeResize: boolean;

  private customerAttributionToken?: string;

  private customerAttributionTransportId?: string;

  private customerAttributionTargetOrigin?: string;

  private customerAttributionParams?: Record<string, unknown>;

  private onCustomerAttributionReleased?: () => void;

  private onCustomerAttributionTokenRefresh?: CustomerAttributionTokenRefresh;

  private onCustomerAttributionTokenRefreshAccepted?: (token: string) => void;

  constructor({
    src,
    containerId,
    queryParams,
    enableIframeResize = true,
    onCustomerAttributionReleased,
    onCustomerAttributionTokenRefresh,
    onCustomerAttributionTokenRefreshAccepted,
  }: WidgetOptions) {
    super();
    this.enableIframeResize = enableIframeResize;

    if (containerId) {
      this.iframeContainer = document.getElementById(
        containerId
      ) as HTMLDivElement;

      if (!this.iframeContainer) {
        throw new Error(
          `Element with id ${containerId} was not found. A valid element id must be provided to embed a widget`
        );
      }

      this.iframeContainer.classList.add("showpass-widget-embedded-wrapper");
      this.iframeContainer.classList.add("showpass-is-loading");
      this.isEmbedded = true;
    } else {
      this.iframeContainer = document.createElement("div");
      this.iframeContainer.classList.add("showpass-widget-wrapper");
      this.iframeContainer.classList.add("showpass-is-loading");
    }

    this.iFrame = document.createElement("iframe");
    this.iFrame.setAttribute("id", widgetID);

    // Allow the 'payment' feature to be available in this document and all nested browsing contexts (iframes)
    // regardless of their origin. This allows browsers with strict context controls like Safari to
    // expect things like 3DS Auth iframes to spawn in _within_ a showpass.com iframe.
    this.iFrame.setAttribute("allow", "payment *; storage-access *");
    this.iFrame.classList.add(iFrameClass);

    const safeQueryParams = normalizeWidgetParams(queryParams ?? {});
    const customerAttributionToken =
      safeQueryParams[CUSTOMER_ATTRIBUTION_QUERY_PARAM];
    delete safeQueryParams[CUSTOMER_ATTRIBUTION_QUERY_PARAM];

    if (
      typeof customerAttributionToken === "string" &&
      customerAttributionToken.trim().length > 0
    ) {
      const transportId = createCustomerAttributionTransportId();
      const parentOrigin = getCustomerAttributionParentOrigin();
      if (transportId && parentOrigin) {
        this.customerAttributionToken = customerAttributionToken.trim();
        this.customerAttributionTransportId = transportId;
        this.customerAttributionParams = queryParams;
        this.onCustomerAttributionReleased = onCustomerAttributionReleased;
        this.onCustomerAttributionTokenRefresh =
          onCustomerAttributionTokenRefresh;
        this.onCustomerAttributionTokenRefreshAccepted =
          onCustomerAttributionTokenRefreshAccepted;
        safeQueryParams[CUSTOMER_ATTRIBUTION_TRANSPORT_ID_QUERY_PARAM] =
          transportId;
        safeQueryParams[CUSTOMER_ATTRIBUTION_PARENT_ORIGIN_QUERY_PARAM] =
          parentOrigin;
      }
    }

    // The shared serializer omits falsy values. Explicit false is meaningful
    // for keep_shopping, show_description and other public widget options.
    const serializedParams = Object.fromEntries(
      Object.entries({ ...safeQueryParams, is_embedded: this.isEmbedded }).map(
        ([key, value]) => [
          key,
          typeof value === "boolean" ? String(value) : value,
        ]
      )
    );
    this.iFrame.src = `${src}?${queryParamSerializer(serializedParams)}`;
    this.customerAttributionTargetOrigin = new URL(this.iFrame.src).origin;

    this.spinner = document.createElement("div");
    this.spinner.classList.add("showpass-spinner");
    const themeColor = safeQueryParams.theme_primary as string | undefined;
    if (themeColor) {
      this.iframeContainer.style.setProperty(
        "--showpass-theme-primary",
        `#${themeColor.replace(/^#/, "")}`
      );
    }
    this.iframeContainer.appendChild(this.spinner);

    this.iFrame.addEventListener("load", () => {
      this.iframeContainer.classList.remove("showpass-is-loading");
      this.spinner.remove();
    });
  }

  private mount = () => {
    if (this.isEmbedded) {
      this.iframeContainer.appendChild(this.iFrame);

      if (this.enableIframeResize) {
        // Set up iframe height listener with wheel event handling
        this.cleanupHeightListener = setupIframeHeightListener(this.iFrame, {
          enableScrollEvents: true,
        });
      }
    }
  };

  unmount = () => {
    if (this.isEmbedded && this.iFrame.parentNode === this.iframeContainer) {
      this.iFrame.remove();
    }

    this.cleanupHeightListener();
    this.cleanupHeightListener = noop;
  };

  remount = () => {
    this.unmount();
    this.mount();
  };

  show = () => {
    window.addEventListener("message", this.handleMessage);

    this[this.isEmbedded ? "mount" : "open"]();

    return this;
  };

  dispose = ({ removeBodyClass = true }: CloseOptions = {}) => {
    if (!this.isEmbedded) {
      this.close({ removeBodyClass });
      return;
    }

    this.removeAllListeners();
    window.removeEventListener("message", this.handleMessage);
    this.clearCustomerAttributionTransport();
    this.unmount();
    this.spinner.remove();
  };

  private open = () => {
    this.isModalOpen = true;
    this.iframeContainer.appendChild(this.iFrame);
    this.scrollTop = window.pageYOffset;

    document.body.appendChild(this.iframeContainer);
    // Animation starts automatically due to CSS
    document.documentElement.classList.add(bodyClass);
    document.body.style.top = `${-this.scrollTop}px`;

    return this;
  };

  private handleMessage = (event: MessageEvent) => {
    if (isIframeUtilMessage(event)) return;
    if (this.handleCustomerAttributionMessage(event)) return;

    switch (event.data) {
      case "showpass.widget.close":
      case "showpass.calendar.close":
      case "showpass.checkout.close":
      case "showpass.security.close":
      case "showpass.product-purchase.close":
      case "showpass.shopping-cart.close":
        this.emit(event.data as WidgetDatalessEvents);
        this.close();
        break;
      default:
        if (event.data?.id) {
          this.emit(event.data.id, event.data);
        } else {
          this.emit(event.data as WidgetDatalessEvents);
        }
        break;
    }
  };

  private handleCustomerAttributionMessage = (event: MessageEvent): boolean => {
    if (
      event.data?.type === CUSTOMER_ATTRIBUTION_REFRESH_REQUEST_MESSAGE &&
      !isCustomerAttributionMessage(event.data)
    ) {
      return true;
    }
    if (!isCustomerAttributionMessage(event.data)) return false;

    const {
      customerAttributionTargetOrigin,
      customerAttributionToken,
      customerAttributionTransportId,
    } = this;
    if (
      !customerAttributionTargetOrigin ||
      !customerAttributionTransportId ||
      event.data.transportId !== customerAttributionTransportId ||
      event.source !== this.iFrame.contentWindow ||
      event.origin !== customerAttributionTargetOrigin
    ) {
      return true;
    }

    if (
      event.data.type === CUSTOMER_ATTRIBUTION_READY_MESSAGE &&
      customerAttributionToken
    ) {
      this.iFrame.contentWindow?.postMessage(
        {
          type: CUSTOMER_ATTRIBUTION_INITIALIZE_MESSAGE,
          transportId: customerAttributionTransportId,
          token: customerAttributionToken,
        },
        customerAttributionTargetOrigin
      );
      return true;
    }

    if (event.data.type === CUSTOMER_ATTRIBUTION_REFRESH_REQUEST_MESSAGE) {
      this.handleCustomerAttributionRefreshRequest(event.data).catch(
        () => undefined
      );
      return true;
    }

    if (
      event.data.type === CUSTOMER_ATTRIBUTION_ESTABLISHED_MESSAGE ||
      event.data.type === CUSTOMER_ATTRIBUTION_DISCARDED_MESSAGE
    ) {
      if (this.customerAttributionParams) {
        delete this.customerAttributionParams[CUSTOMER_ATTRIBUTION_QUERY_PARAM];
      }
      this.onCustomerAttributionReleased?.();
      if (event.data.type === CUSTOMER_ATTRIBUTION_DISCARDED_MESSAGE) {
        this.clearCustomerAttributionToken();
      } else {
        this.clearCustomerAttributionTransport();
      }
    }

    return true;
  };

  private handleCustomerAttributionRefreshRequest = async (
    request: CustomerAttributionRefreshRequest
  ): Promise<void> => {
    this.emit("showpass.widget.checkout-reset");

    let token: string | undefined;
    const refresh = this.onCustomerAttributionTokenRefresh;
    if (refresh) {
      const remainingMs = Math.max(request.expiresAt - Date.now(), 0);
      token = await new Promise<string | undefined>((resolve) => {
        let settled = false;
        const timeoutId = window.setTimeout(() => {
          settled = true;
          resolve(undefined);
        }, remainingMs);

        Promise.resolve()
          .then(() => refresh())
          .then(
            (value) => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeoutId);
              const normalized =
                typeof value === "string" ? value.trim() : undefined;
              resolve(normalized || undefined);
            },
            () => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeoutId);
              resolve(undefined);
            }
          );
      });
    }

    if (token && Date.now() >= request.expiresAt) {
      token = undefined;
    }

    if (
      this.customerAttributionTransportId !== request.transportId ||
      !this.customerAttributionTargetOrigin ||
      !this.iFrame.contentWindow
    ) {
      return;
    }

    const targetOrigin = this.customerAttributionTargetOrigin;
    const iframeWindow = this.iFrame.contentWindow;

    if (token) {
      this.customerAttributionToken = token;
      if (this.customerAttributionParams) {
        this.customerAttributionParams[CUSTOMER_ATTRIBUTION_QUERY_PARAM] =
          token;
      }
      try {
        this.onCustomerAttributionTokenRefreshAccepted?.(token);
      } catch {
        this.clearCustomerAttributionToken();
        iframeWindow.postMessage(
          {
            type: CUSTOMER_ATTRIBUTION_REFRESH_UNAVAILABLE_MESSAGE,
            transportId: request.transportId,
            requestId: request.requestId,
          },
          targetOrigin
        );
        return;
      }
      iframeWindow.postMessage(
        {
          type: CUSTOMER_ATTRIBUTION_REFRESH_RESPONSE_MESSAGE,
          transportId: request.transportId,
          requestId: request.requestId,
          token,
        },
        targetOrigin
      );
      return;
    }

    this.clearCustomerAttributionToken();
    this.iFrame.contentWindow.postMessage(
      {
        type: CUSTOMER_ATTRIBUTION_REFRESH_UNAVAILABLE_MESSAGE,
        transportId: request.transportId,
        requestId: request.requestId,
      },
      targetOrigin
    );
  };

  private clearCustomerAttributionToken = (removeFromParams = true): void => {
    this.customerAttributionToken = undefined;
    if (removeFromParams && this.customerAttributionParams) {
      delete this.customerAttributionParams[CUSTOMER_ATTRIBUTION_QUERY_PARAM];
    }
  };

  private clearCustomerAttributionTransport = (): void => {
    this.clearCustomerAttributionToken(false);
    this.customerAttributionTransportId = undefined;
    this.customerAttributionTargetOrigin = undefined;
    this.customerAttributionParams = undefined;
    this.onCustomerAttributionReleased = undefined;
    this.onCustomerAttributionTokenRefresh = undefined;
    this.onCustomerAttributionTokenRefreshAccepted = undefined;
  };

  close = ({ removeBodyClass = true, remount = true }: CloseOptions = {}) => {
    if (this.isEmbedded && remount) {
      this.remount();
    }

    // this'll also catch when we're embedded since this.isVisible is only set when the modal opens
    if (!this.isModalOpen) {
      return;
    }

    this.isModalOpen = false;
    this.removeAllListeners();
    window.removeEventListener("message", this.handleMessage);
    this.clearCustomerAttributionTransport();

    if (this.cleanupHeightListener) {
      this.cleanupHeightListener();
    }

    // Add closing class and wait for animation
    this.iframeContainer.classList.add("closing");

    setTimeout(() => {
      document.body.removeChild(this.iframeContainer);

      if (removeBodyClass) {
        document.documentElement.classList.remove(bodyClass);
      }
      document.body.style.top = "auto";
      window.scrollTo(0, this.scrollTop);
    }, animationDuration - 1);
  };
}
